// PWA plumbing: registers the service worker (see /sw.js) that makes the app
// installable and usable offline, and keeps the browser/OS chrome
// colour (<meta name="theme-color">) in step with the active theme.

// True when launched from the installed icon rather than a browser tab.
// navigator.standalone is iOS Safari's own flag (older versions don't answer
// the display-mode media query).
export function isInstalledApp() {
    return window.matchMedia('(display-mode: standalone)').matches
        || window.navigator.standalone === true;
}

export function syncThemeColor() {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) return;
    const background = getComputedStyle(document.documentElement).getPropertyValue('--bg').trim();
    if (background) meta.setAttribute('content', background);
}

let registration = null;
let waitingWorker = null;
let reloadOnControllerChange = false;
let runningVersion = null; // the version this page was loaded as - the first /api/version answer we saw
let updateHandlers = { onReady() {}, onRequired() {} };
let lastCheckAt = 0;

const CHECK_INTERVAL_MS = 30 * 60 * 1000;
const CHECK_THROTTLE_MS = 60 * 1000;
const APPLY_FALLBACK_MS = 3000;

export function registerServiceWorker() {
    if (!('serviceWorker' in navigator)) return;
    const register = () => navigator.serviceWorker.register('sw.js').then(trackUpdates, () => { /* not installable here - the app itself is unaffected */ });
    if (document.readyState === 'complete') register();
    else window.addEventListener('load', register);
}

// The service worker (see /sw.js) installs a new version in the background
// and then waits, so a deploy never swaps code under a game in progress. We
// tell the app once one is waiting (onReady), and applyUpdate() releases it.
function trackUpdates(reg) {
    registration = reg;
    const announce = () => {
        // No controller means this is the first install - nothing to update from.
        if (reg.waiting && navigator.serviceWorker.controller) {
            waitingWorker = reg.waiting;
            updateHandlers.onReady();
        }
    };
    announce();
    reg.addEventListener('updatefound', () => {
        const worker = reg.installing;
        if (worker) worker.addEventListener('statechange', () => { if (worker.state === 'installed') announce(); });
    });
    navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (reloadOnControllerChange) window.location.reload();
    });
}

export function isUpdateReady() {
    return !!waitingWorker;
}

// Swaps in the waiting worker and reloads onto it. With nothing waiting (the
// "required" case on a browser without a worker) a plain reload is all there is.
export function applyUpdate() {
    if (!waitingWorker) {
        window.location.reload();
        return;
    }
    reloadOnControllerChange = true;
    waitingWorker.postMessage('SKIP_WAITING');
    setTimeout(() => window.location.reload(), APPLY_FALLBACK_MS);
}

function compareVersions(a, b) {
    const pa = String(a).split('.').map(Number);
    const pb = String(b).split('.').map(Number);
    for (let i = 0; i < 3; i++) {
        const diff = (pa[i] || 0) - (pb[i] || 0);
        if (diff) return diff;
    }
    return 0;
}

// GET /api/version -> { version, minClientVersion, dev }, or null when it
// can't be reached. The first answer is remembered as the version this page is running.
export async function fetchVersionInfo() {
    try {
        const res = await fetch('/api/version', { cache: 'no-store' });
        const info = await res.json();
        if (runningVersion === null) runningVersion = info.version;
        return info;
    } catch (e) {
        return null;
    }
}

export function getRunningVersion() {
    return runningVersion;
}

// One check, two mechanisms: ask the browser for a newer worker (catches every
// deploy, even ones that don't bump the version), and compare /api/version
// (catches a server that no longer speaks to this client). Resolves to
// 'ready' (an update is waiting), 'required' (this client is too old to keep
// running), 'current', or 'error' (offline / unreachable).
export async function checkForUpdates() {
    lastCheckAt = Date.now();
    const info = await fetchVersionInfo();
    if (!info) return 'error';
    if (info.minClientVersion && compareVersions(runningVersion, info.minClientVersion) < 0) {
        updateHandlers.onRequired();
        return 'required';
    }
    if (registration && !info.dev) {
        try { await registration.update(); } catch (e) { return 'error'; }
        // update() resolves once the new worker has been fetched; give install a moment to finish
        if (!waitingWorker && info.version !== runningVersion) await waitForInstall(registration);
    }
    return waitingWorker ? 'ready' : 'current';
}

function waitForInstall(reg) {
    const worker = reg.installing;
    if (!worker || worker.state === 'installed') return Promise.resolve();
    return new Promise((resolve) => {
        worker.addEventListener('statechange', () => { if (worker.state === 'installed' || worker.state === 'redundant') resolve(); });
        setTimeout(resolve, 15000);
    });
}

// Checks whenever the app comes back to the foreground (the case that matters
// on a tablet, where the installed app stays alive for days), when the
// connection returns, and on a slow timer. Throttled so they don't stack.
export function watchForUpdates(handlers) {
    updateHandlers = { ...updateHandlers, ...handlers };
    const maybeCheck = () => {
        if (Date.now() - lastCheckAt >= CHECK_THROTTLE_MS) checkForUpdates();
    };
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') maybeCheck(); });
    window.addEventListener('online', maybeCheck);
    setInterval(maybeCheck, CHECK_INTERVAL_MS);
}

// Stylesheets may not have applied yet at module-evaluation time, so the
// initial sync waits for load, same as registration above.
export function initPwa() {
    document.documentElement.classList.toggle('pwa-standalone', isInstalledApp());
    registerServiceWorker();
    if (document.readyState === 'complete') syncThemeColor();
    else window.addEventListener('load', syncThemeColor);
}
